import { createUseStyles } from "react-jss";
import { colors } from "../../styles/colors";
import { Z } from "../../styles/zIndex";

const useStyles = createUseStyles({
  root: {
    overflow: "hidden",
    // Anchor to the viewport, not a percentage chain: html/body have no explicit
    // height, so `height: 100%` resolved to `auto`, the page grew with content,
    // scrolling moved to the window, and the thread's own scroll never engaged.
    height: "var(--app-vh)",
    "@media (max-width:899.95px)": {
      height: "auto",
      minHeight: "var(--app-vh)",
    },
  },
  sidebarOuter: {
    height: "var(--app-vh)",
    "@media (max-width:899.95px)": {
      height: "auto",
      minHeight: "var(--app-vh)",
    },
  },
  page: {
    height: "100%",
    display: "flex",
    backgroundColor: colors.bgcolor,
    overflow: "hidden",
    "@media (max-width:899.95px)": {
      flexDirection: "column",
      height: "100dvh",
      paddingTop: "56px",
    },
  },
  // â”€â”€ Conversation rail â”€â”€
  convoRail: {
    width: "260px",
    minWidth: "220px",
    flexShrink: 0,
    height: "100%",
    borderRight: `1px solid ${colors.borderLight}`,
    backgroundColor: colors.surface,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    "@media (max-width: 1100px)": {
      width: "220px",
      minWidth: "180px",
    },
    "@media (max-width:899.95px)": {
      width: "100%",
      minWidth: 0,
      height: "auto",
      maxHeight: "52px",
      flexShrink: 0,
      borderRight: "none",
      borderBottom: `1px solid ${colors.borderLight}`,
      flexDirection: "row",
      alignItems: "center",
      padding: "0 12px",
      gap: "10px",
    },
  },
  convoRailHeader: {
    padding: "16px 14px 10px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    flexShrink: 0,
    "@media (max-width:899.95px)": {
      padding: 0,
      flexShrink: 0,
    },
  },
  newChatBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    height: "42px",
    borderRadius: "12px",
    cursor: "pointer",
    color: "#F8FAFC",
    fontFamily: "Poppins-Medium",
    fontSize: "13.5px",
    background: "linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)",
    border: "none",
    transition: "transform 140ms ease, box-shadow 140ms ease",
    boxShadow: "0 6px 18px rgba(37,99,235,0.28)",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 26px rgba(37,99,235,0.36)",
    },
    "&:focus-visible": {
      outline: `2px solid ${colors.fleetBlue}`,
      outlineOffset: "2px",
    },
    "@media (max-width:899.95px)": {
      height: "36px",
      padding: "0 14px",
      fontSize: "12.5px",
      whiteSpace: "nowrap",
    },
  },
  convoList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "4px 8px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    "@media (max-width:899.95px)": {
      flex: 1,
      minWidth: 0,
      flexDirection: "row",
      overflowY: "hidden",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      gap: "6px",
      padding: "0 4px",
      "& > div": { flexShrink: 0, maxWidth: "160px" },
    },
    "&::-webkit-scrollbar": { width: "5px" },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "6px",
      backgroundColor: "rgba(148,163,184,0.28)",
    },
  },
  convoItem: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    color: colors.secondaryTextColor,
    transition: "background-color 140ms ease, color 140ms ease",
    width: "100%",
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: colors.surfaceHover,
      color: colors.primaryText,
    },
    "&:hover $convoDelete": { opacity: 1 },
    "&:focus-visible": {
      outline: `2px solid ${colors.fleetBlue}`,
      outlineOffset: "-2px",
    },
    "&:focus-within $convoDelete": { opacity: 1 },
    "@media (max-width:899.95px)": {
      padding: "6px 10px",
      gap: "6px",
      borderRadius: "8px",
      border: `1px solid ${colors.borderLight}`,
    },
  },
  convoItemActive: {
    backgroundColor: "rgba(37,99,235,0.12)",
    color: colors.primaryText,
    "& $convoTitle": { fontFamily: "Poppins-Medium" },
  },
  convoTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: "13px",
    fontFamily: "Poppins-Regular",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  convoDelete: {
    opacity: 0,
    display: "grid",
    placeItems: "center",
    width: "26px",
    height: "26px",
    borderRadius: "7px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    transition: "opacity 140ms ease, background-color 140ms ease",
    "&:hover": { backgroundColor: "rgba(239,68,68,0.16)" },
    "&:focus-visible": {
      opacity: 1,
      outline: `2px solid ${colors.fleetBlue}`,
      outlineOffset: "1px",
    },
  },
  railFooter: {
    padding: "10px 14px",
    borderTop: `1px solid ${colors.borderLight}`,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
    "@media (max-width:899.95px)": {
      display: "none",
    },
  },
  modelDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: colors.successColor,
    boxShadow: `0 0 0 3px ${colors.successColor}22`,
  },
  // â”€â”€ Chat column â”€â”€
  chatCol: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    "@media (max-width:899.95px)": {
      flex: 1,
      minHeight: 0,
    },
  },
  chatHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 20px",
    borderBottom: `1px solid ${colors.borderLight}`,
    backgroundColor: colors.bgcolor,
    flexShrink: 0,
    flexWrap: "nowrap",
    overflow: "hidden",
    "@media (max-width:899.95px)": {
      padding: "10px 14px",
      flexWrap: "wrap",
    },
  },
  modeSwitch: {
    marginLeft: "auto",
    display: "flex",
    gap: "6px",
    padding: "4px",
    borderRadius: "12px",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
    "@media (max-width:899.95px)": {
      marginLeft: 0,
      width: "100%",
      justifyContent: "center",
      "& > button": { flex: 1, textAlign: "center" },
    },
  },
  modeChip: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: "7px 14px",
    borderRadius: "9px",
    fontFamily: "Poppins-Medium",
    fontSize: "12.5px",
    color: colors.secondaryTextColor,
    transition: "background 120ms ease, color 120ms ease",
    whiteSpace: "nowrap",
    "&:hover": { color: colors.primaryText },
    "&:disabled": { opacity: 0.55, cursor: "default" },
  },
  modeChipActive: {
    color: "#F8FAFC",
    background: "linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)",
    boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
  },
  avatar: {
    width: "38px",
    height: "38px",
    borderRadius: "11px",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    background: "linear-gradient(135deg, #2563EB 0%, #60A5FA 100%)",
    boxShadow: "0 4px 14px rgba(37,99,235,0.32)",
  },
  thread: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    // NOTE: no `overscroll-behavior: contain` here â€” with it, Chrome swallows
    // wheel events on this container even when it has nothing to scroll,
    // which made mouse-wheel scrolling appear completely dead.
    scrollBehavior: "auto",
    padding: "28px clamp(16px, 5vw, 64px)",
    "@media (max-width:899.95px)": {
      padding: "16px 14px",
    },
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "6px",
      backgroundColor: "rgba(148,163,184,0.28)",
    },
  },
  threadInner: {
    maxWidth: "780px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "22px",
  },
  // â”€â”€ Empty / hero state â”€â”€
  hero: {
    maxWidth: "760px",
    margin: "0 auto",
    paddingTop: "6vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "10px",
    "@media (max-width:899.95px)": {
      paddingTop: "3vh",
    },
  },
  heroBadge: {
    width: "64px",
    height: "64px",
    borderRadius: "20px",
    display: "grid",
    placeItems: "center",
    marginBottom: "8px",
    background: "linear-gradient(135deg, #2563EB 0%, #60A5FA 100%)",
    boxShadow: "0 12px 34px rgba(37,99,235,0.4)",
  },
  capGrid: {
    width: "100%",
    marginTop: "26px",
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "14px",
    "@media (max-width: 720px)": {
      gridTemplateColumns: "1fr",
    },
  },
  capCard: {
    display: "flex",
    gap: "14px",
    alignItems: "flex-start",
    padding: "16px",
    borderRadius: "14px",
    textAlign: "left",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
  },
  capIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "11px",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    backgroundColor: "rgba(37,99,235,0.12)",
  },
  // â”€â”€ Messages â”€â”€
  row: {
    display: "flex",
    gap: "14px",
    alignItems: "flex-start",
  },
  rowUser: {
    flexDirection: "row-reverse",
  },
  msgAvatar: {
    width: "32px",
    height: "32px",
    borderRadius: "9px",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },
  msgAvatarAi: {
    background: "linear-gradient(135deg, #2563EB 0%, #60A5FA 100%)",
  },
  msgAvatarUser: {
    backgroundColor: colors.surfaceElevated,
    border: `1px solid ${colors.borderLight}`,
  },
  bubble: {
    maxWidth: "100%",
    padding: "13px 16px",
    borderRadius: "14px",
    fontSize: "14px",
    lineHeight: 1.62,
    fontFamily: "Poppins-Regular",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  bubbleAi: {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
    color: colors.primaryText,
    borderTopLeftRadius: "4px",
    "& strong": {
      fontFamily: "Poppins-Medium",
      fontWeight: 500,
    },
    "& em": {
      fontStyle: "italic",
    },
    "& li": {
      lineHeight: 1.7,
    },
    "& code": {
      fontFamily: "monospace",
    },
  },
  bubbleUser: {
    background: "linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)",
    color: "#F8FAFC",
    borderTopRightRadius: "4px",
  },
  msgWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
    maxWidth: "76%",
    "@media (max-width:599.95px)": { maxWidth: "86%" },
  },
  msgWrapUser: { alignItems: "flex-end" },
  // â”€â”€ Tool trace â”€â”€
  trace: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "10px 12px",
    borderRadius: "12px",
    backgroundColor: colors.surfaceElevated,
    border: `1px solid ${colors.borderLight}`,
  },
  traceRow: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    fontSize: "12.5px",
    color: colors.secondaryTextColor,
    fontFamily: "Poppins-Regular",
  },
  // â”€â”€ Citations â”€â”€
  citations: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "2px",
  },
  citation: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontFamily: "Poppins-Medium",
    cursor: "pointer",
    color: colors.fleetBlue,
    backgroundColor: "rgba(37,99,235,0.1)",
    border: "1px solid rgba(37,99,235,0.22)",
    transition: "background-color 140ms ease",
    "&:hover": { backgroundColor: "rgba(37,99,235,0.18)" },
  },
  // â”€â”€ Typing dots â”€â”€
  typing: { display: "inline-flex", gap: "4px", padding: "4px 2px" },
  dot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: colors.electricBlue,
    animation: "$blink 1.2s infinite ease-in-out",
  },
  "@keyframes blink": {
    "0%, 80%, 100%": { opacity: 0.2, transform: "translateY(0)" },
    "40%": { opacity: 1, transform: "translateY(-3px)" },
  },
  // â”€â”€ Suggested prompts â”€â”€
  suggestRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "center",
    marginTop: "22px",
  },
  suggestChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 14px",
    borderRadius: "999px",
    cursor: "pointer",
    fontSize: "13px",
    fontFamily: "Poppins-Medium",
    color: colors.primaryText,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
    transition: "border-color 140ms ease, transform 140ms ease",
    "&:hover": {
      borderColor: "rgba(37,99,235,0.45)",
      transform: "translateY(-1px)",
    },
    "&:focus-visible": {
      outline: `2px solid ${colors.fleetBlue}`,
      outlineOffset: "2px",
    },
  },
  // â”€â”€ Composer â”€â”€
  composerWrap: {
    padding: "14px clamp(16px, 5vw, 64px) 22px",
    flexShrink: 0,
    "@media (max-width:899.95px)": {
      padding: "10px 12px calc(12px + env(safe-area-inset-bottom))",
    },
    backgroundColor: colors.bgcolor,
  },
  composer: {
    maxWidth: "780px",
    margin: "0 auto",
    display: "flex",
    alignItems: "flex-end",
    gap: "10px",
    padding: "8px 8px 8px 18px",
    borderRadius: "16px",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
    transition: "border-color 160ms ease, box-shadow 160ms ease",
  },
  composerFocused: {
    borderColor: "rgba(37,99,235,0.55)",
    boxShadow: "0 0 0 4px rgba(37,99,235,0.12)",
  },
  input: {
    flex: 1,
    border: "none",
    outline: "none",
    resize: "none",
    background: "transparent",
    color: colors.primaryText,
    fontFamily: "Poppins-Regular",
    fontSize: "14px",
    lineHeight: 1.5,
    maxHeight: "168px",
    padding: "9px 0",
    "&::placeholder": { color: colors.mutedText },
  },
  sendBtn: {
    width: "42px",
    height: "42px",
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    flexShrink: 0,
    border: "none",
    color: "#F8FAFC",
    background: "linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)",
    transition: "opacity 140ms ease, transform 140ms ease",
    "&:hover": { transform: "scale(1.04)" },
    "&:focus-visible": {
      outline: `2px solid ${colors.fleetBlue}`,
      outlineOffset: "2px",
    },
  },
  sendBtnDisabled: {
    cursor: "not-allowed",
    opacity: 0.45,
    background: colors.surfaceElevated,
    color: colors.mutedText,
    "&:hover": { transform: "none" },
  },
  // â”€â”€ Tab bar (Chat / Reports / Approvals / Memory) â”€â”€
  tabBar: {
    display: "flex",
    gap: "2px",
    padding: "0 24px",
    borderBottom: `1px solid ${colors.borderLight}`,
    backgroundColor: colors.bgcolor,
    flexShrink: 0,
    // Keep overflow visible on desktop so the locked-tab tooltip isn't clipped;
    // horizontal scroll only kicks in on narrow screens (no hover tooltips there).
    overflow: "visible",
    "&::-webkit-scrollbar": { display: "none" },
    "@media (max-width:899.95px)": {
      padding: "0 12px",
      overflowX: "auto",
    },
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "11px 16px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontFamily: "Poppins-Medium",
    fontSize: "13px",
    color: colors.secondaryTextColor,
    borderBottom: "2px solid transparent",
    transition: "color 140ms ease, border-color 140ms ease",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: colors.primaryText },
    "&:focus-visible": {
      outline: `2px solid ${colors.fleetBlue}`,
      outlineOffset: "-2px",
      borderRadius: "6px",
    },
  },
  tabActive: {
    color: colors.fleetBlue,
    borderBottomColor: colors.fleetBlue,
  },
  tabLocked: {
    opacity: 0.45,
    "&:hover": { color: colors.mutedText },
  },
  tabTooltip: {
    position: "absolute",
    bottom: "calc(100% + 8px)",
    left: "50%",
    transform: "translateX(-50%)",
    padding: "10px 16px",
    borderRadius: "10px",
    fontSize: "12.5px",
    fontFamily: "Poppins-Medium",
    lineHeight: 1.45,
    color: "#F8FAFC",
    background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
    boxShadow: "0 8px 24px rgba(15,23,42,0.32)",
    zIndex: Z.OVERLAY,
    pointerEvents: "none",
    maxWidth: "340px",
    whiteSpace: "normal" as any,
    textAlign: "center" as const,
    "&::after": {
      content: '""',
      position: "absolute",
      top: "100%",
      left: "50%",
      transform: "translateX(-50%)",
      border: "6px solid transparent",
      borderTopColor: "#0F172A",
    },
  },
  tabBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "20px",
    height: "20px",
    borderRadius: "999px",
    padding: "0 6px",
    fontSize: "11px",
    fontFamily: "Poppins-Medium",
    color: "#F8FAFC",
    background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
  },
  disclaimer: {
    maxWidth: "780px",
    margin: "8px auto 0",
    textAlign: "center",
    fontSize: "11.5px",
    color: colors.mutedText,
    fontFamily: "Poppins-Regular",
  },
});

export default useStyles;